import React, { useEffect, useRef, useState } from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { useSelector, useDispatch } from 'react-redux';
import { Box, CircularProgress, Typography } from '@mui/material';
import { RootState } from '../store';
import { useGetMeQuery } from '../services/auth.service';
import { setCredentials, setLoading, logout } from '../store/slices/authSlice';

interface PrivateRouteProps {
  children: React.ReactNode;
}

const PrivateRoute: React.FC<PrivateRouteProps> = ({ children }) => {
  const dispatch = useDispatch();
  const location = useLocation();
  const { isAuthenticated, loading, accessToken, user } = useSelector((state: RootState) => state.auth);
  const retryCountRef = useRef(0);
  const maxRetries = 2;
  const [authCheckComplete, setAuthCheckComplete] = useState(false);
  const timeoutRef = useRef<NodeJS.Timeout | null>(null);
  
  const {
    data: fetchedUser,
    isLoading: isMeLoading,
    error: meError,
    refetch: refetchMe,
  } = useGetMeQuery(undefined, {
    skip: !accessToken || isAuthenticated,
    // Add retry logic to prevent excessive requests
    refetchOnReconnect: false,
    refetchOnFocus: false,
    // Prevent polling that could cause loops
    pollingInterval: 0,
  });

  // Set timeout to prevent infinite loading
  useEffect(() => {
    if (isMeLoading && !timeoutRef.current) {
      timeoutRef.current = setTimeout(() => {
        console.warn('Authentication check timeout, forcing logout');
        dispatch(logout());
        setAuthCheckComplete(true);
      }, 15000); // 15 second timeout
    } else if (!isMeLoading && timeoutRef.current !== null) {
      clearTimeout(timeoutRef.current);
      timeoutRef.current = null;
    }

    return () => {
      if (timeoutRef.current !== null) {
        clearTimeout(timeoutRef.current);
        timeoutRef.current = null;
      }
    };
  }, [isMeLoading, dispatch]);

  useEffect(() => {
    // If there's no access token, we can't be authenticated
    if (!accessToken) {
      dispatch(setLoading(false));
      setAuthCheckComplete(true);
      return;
    }
    
    // If we have both access token and fetched user data, set credentials
    if (accessToken && fetchedUser && !isAuthenticated) {
      dispatch(setCredentials({
        user: fetchedUser,
        accessToken,
        refreshToken: localStorage.getItem('refreshToken') || '',
      }));
      retryCountRef.current = 0; // Reset retry count on successful auth
      setAuthCheckComplete(true);
    }
    
    // If we already have a user in state and valid token, we're authenticated
    if (accessToken && user && !authCheckComplete) {
      setAuthCheckComplete(true);
    }
    
    if (!isMeLoading) {
      dispatch(setLoading(false));
      if (!accessToken || (!fetchedUser && !user && authCheckComplete)) {
        setAuthCheckComplete(true);
      }
    }
  }, [accessToken, fetchedUser, user, isAuthenticated, isMeLoading, authCheckComplete, dispatch]);

  useEffect(() => {
    if (meError) {
      retryCountRef.current += 1;
      console.warn(`Authentication check failed (attempt ${retryCountRef.current}/${maxRetries}):`, meError);
      
      if (retryCountRef.current >= maxRetries) {
        // After max retries, logout user
        console.warn('Max authentication retries exceeded, logging out');
        dispatch(logout());
      }
      
      dispatch(setLoading(false));
      setAuthCheckComplete(true);
    }
  }, [meError, dispatch]);

  if ((loading || isMeLoading) && !authCheckComplete) {
    return (
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'center',
          alignItems: 'center',
          height: '100vh',
          gap: 2,
        }}
      >
        <CircularProgress size={60} />
        <Typography variant="body1" color="text.secondary">
          Verificando autenticación...
        </Typography>
        <Typography variant="caption" color="text.secondary" sx={{ textAlign: 'center', maxWidth: 300 }}>
          Si esto toma demasiado tiempo, la sesión puede haber expirado
        </Typography>
      </Box>
    );
  }

  // Only redirect to login if authentication check is complete and we're not authenticated
  if (authCheckComplete && (!isAuthenticated || !accessToken)) {
    // Clear any stale authentication data before redirecting
    if (accessToken && !isAuthenticated) {
      console.warn('Clearing stale authentication data');
      dispatch(logout());
    }
    return <Navigate to="/login" state={{ from: location }} replace />;
  }
  
  // If auth check is not complete yet, don't redirect
  if (!authCheckComplete) {
    return (
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'center',
          alignItems: 'center',
          height: '100vh',
          gap: 2,
        }}
      >
        <CircularProgress size={60} />
        <Typography variant="body1" color="text.secondary">
          Verificando autenticación...
        </Typography>
      </Box>
    );
  }

  return <>{children}</>;
};

export default PrivateRoute;